import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  BanknotesIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import ApiLoader from '../../ui/ApiLoader';
import { DataTable } from '../../common/DataTable';

const BillingSubscriptionsTab = ({ 
  loading, 
  billingData 
}) => {
  const navigate = useNavigate();

  // Calculate metrics from billingData
  const totalRevenue = billingData.reduce((sum, item) => sum + (item.price || 0), 0);
  const monthlyRevenue = totalRevenue; // Assuming prices are monthly
  const annualRevenue = totalRevenue * 12;
  const avgRevenuePerUser = billingData.length > 0 ? totalRevenue / billingData.length : 0;
  const totalCustomers = billingData.length;

  if (loading) {
    return (
      <ApiLoader label="Loading billing data..." className="py-20" />
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          {
            title: 'Monthly Recurring Revenue',
            value: `$${monthlyRevenue.toLocaleString()}`,
            icon: BanknotesIcon,
            color: 'green',
            trend: `${totalCustomers} Customers`
          },
          {
            title: 'Annual Contract Value',
            value: `$${(annualRevenue / 1000000).toFixed(2)}M`,
            icon: ChartBarIcon,
            color: 'blue',
            trend: `Based on MRR`
          },
          {
            title: 'Average Revenue per User',
            value: `$${avgRevenuePerUser.toFixed(0).toLocaleString()}`,
            icon: UserGroupIcon,
            color: 'purple',
            trend: 'Per Customer'
          },
          {
            title: 'Total Customers',
            value: totalCustomers,
            icon: ExclamationTriangleIcon,
            color: 'indigo',
            trend: 'Active Accounts'
          }
        ].map((metric, index) => (
          <motion.div
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{metric.title}</h3>
              <metric.icon className={`w-6 h-6 text-${metric.color}-500`} />
            </div>
            <div className="text-2xl font-bold mb-2">{metric.value}</div>
            <div className={`text-sm font-medium text-${metric.color}-600`}>{metric.trend}</div>
          </motion.div>
        ))}
      </div>

      {/* Subscription Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* All Customers Billing Table */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Customer Billing Overview</h3>
            <button
              onClick={() => navigate('/admin', { state: { activeTab: 'users2' } })}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors group"
            >
              <span>View More</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <DataTable
            columns={[
              {
                id: "company",
                header: "Company",
                cell: ({ row }) => (
                  <div>
                    <div className="text-sm font-medium text-gray-900">{row.original.company_name || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{row.original.user_name || 'N/A'}</div>
                  </div>
                ),
              },
              {
                accessorKey: "price",
                header: "Price",
                cell: ({ getValue }) => (
                  <span className="text-sm font-semibold text-green-600">${(getValue() || 0).toLocaleString()}</span>
                ),
              },
              {
                accessorKey: "renewal_date",
                header: "Renewal",
                cell: ({ getValue }) => <span className="text-sm text-gray-900">{getValue() ? new Date(getValue()).toLocaleDateString() : 'N/A'}</span>,
              },
            ]}
            data={billingData}
            getRowId={(item, index) => String(item.id || index)}
            emptyMessage="No billing data available"
            enableColumnVisibility={false}
            maxBodyHeight="24rem"
          />
        </div>

        {/* Upcoming Renewals */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Upcoming Renewals (Next 30 Days)</h3>
          <div className="space-y-4">
            {(() => {
              // Calculate upcoming renewals (next 30 days)
              const today = new Date();
              const thirtyDaysFromNow = new Date();
              thirtyDaysFromNow.setDate(today.getDate() + 30);
              
              const upcomingRenewals = billingData
                .filter(item => {
                  if (!item.renewal_date) return false;
                  const renewalDate = new Date(item.renewal_date);
                  return renewalDate >= today && renewalDate <= thirtyDaysFromNow;
                })
                .sort((a, b) => new Date(a.renewal_date) - new Date(b.renewal_date))
                .slice(0, 5); // Get only top 5 upcoming renewals

              return upcomingRenewals.length > 0 ? (
                upcomingRenewals.map((renewal, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">{renewal.company_name}</h4>
                      <div className="text-sm text-gray-500">Renews: {new Date(renewal.renewal_date).toLocaleDateString()}</div>
                      <div className="text-xs text-gray-400">{renewal.user_name}</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-semibold text-green-600">
                        ${(renewal.price || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No upcoming renewals in the next 30 days
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingSubscriptionsTab;
